import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ShieldCheck, ChartBar, ChartLineUp, Receipt, ChatCircleText, Users, Tag,
  CurrencyEur, Package, MagnifyingGlass, TruckTrailer, CheckCircle, X, Plus, Trash, PencilSimple, Warning, Clock, Eye
} from "@phosphor-icons/react";
import { toast } from "sonner";
import SupplierSearchPanel from "@/components/SupplierSearchPanel";

const ORDER_STATUS = [
  { v: "pending", label: "En attente paiement", color: "border-zinc-400 text-zinc-500" },
  { v: "paid", label: "Payée — à préparer", color: "border-amber-400 text-amber-600" },
  { v: "preparing", label: "En préparation", color: "border-blue-400 text-blue-600" },
  { v: "shipped", label: "Expédiée", color: "border-purple-400 text-purple-600" },
  { v: "delivered", label: "Livrée", color: "border-[#00E676] text-[#00A050]" },
  { v: "cancelled", label: "Annulée", color: "border-red-400 text-red-600" },
];
const statusLabel = (s) => ORDER_STATUS.find(x => x.v === s)?.label || s;
const statusColor = (s) => ORDER_STATUS.find(x => x.v === s)?.color || "border-zinc-300 text-zinc-500";

export default function Admin() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") === "suppliers" ? "suppliers" : "dashboard");
  useEffect(() => { if (searchParams.get("tab") === "suppliers") setTab("suppliers"); }, [searchParams]);
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [discForm, setDiscForm] = useState({ category: "", percent: 10, label: "", active: true });

  const { login } = useAuth();
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  useEffect(() => {
    if (user && user.role !== "admin") { nav("/"); return; }
    if (user?.role === "admin") refreshAll();
  }, [user, nav]);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true); setLoginError("");
    try {
      const u = await login(loginForm.email, loginForm.password);
      if (u?.role !== "admin") { setLoginError("Accès réservé aux administrateurs."); }
    } catch { setLoginError("Email ou mot de passe incorrect."); }
    finally { setLoginLoading(false); }
  };

  const refreshAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/orders").then((r) => setOrders(r.data || []));
    api.get("/admin/quotes").then((r) => setQuotes(r.data || []));
    api.get("/admin/customers").then((r) => setCustomers(r.data || []));
    api.get("/admin/discounts").then((r) => setDiscounts(r.data || []));
    api.get("/admin/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  };

  if (!user || user.role !== "admin") return null;

  // ----- Orders -----
  const updateOrderStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}`, { status });
      toast.success("Statut mis à jour");
      api.get("/admin/orders").then((r) => setOrders(r.data || []));
      api.get("/admin/stats").then((r) => setStats(r.data));
      if (selectedOrder?.id === id) setSelectedOrder({ ...selectedOrder, status });
    } catch (e) { toast.error("Erreur"); }
  };
  const saveOrderTracking = async (id, fields) => {
    try {
      await api.patch(`/admin/orders/${id}`, fields);
      toast.success("Enregistré");
      api.get("/admin/orders").then((r) => setOrders(r.data || []));
    } catch (e) { toast.error("Erreur"); }
  };
  const retryFerebamOrder = async (id) => {
    try {
      await api.post(`/admin/orders/${id}/sync-odoo`);
      toast.success("Commande synchronisée dans Odoo");
      const { data } = await api.get("/admin/orders");
      setOrders(data || []);
      const refreshed = (data || []).find((o) => o.id === id);
      if (refreshed) setSelectedOrder(refreshed);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur de synchronisation Odoo"); }
  };
  const markOrderReceived = async (id) => {
    try {
      const { data } = await api.post(`/admin/orders/${id}/received`);
      toast.success("Pièce marquée reçue — prête à expédier");
      api.get("/admin/orders").then((r) => setOrders(r.data || []));
      if (selectedOrder?.id === id) setSelectedOrder(data);
    } catch (e) { toast.error("Erreur"); }
  };

  // ----- Quotes -----
  const respondQuote = async (id, fields) => {
    try {
      const { data } = await api.patch(`/admin/quotes/${id}`, fields);
      toast.success("Devis enregistré");
      api.get("/admin/quotes").then((r) => setQuotes(r.data || []));
      api.get("/admin/stats").then((r) => setStats(r.data));
      setSelectedQuote(data);
    } catch (e) { toast.error("Erreur"); }
  };

  // ----- Discounts -----
  const addDiscount = async (e) => {
    e.preventDefault();
    if (!discForm.category) return;
    try {
      const { data } = await api.post("/admin/discounts", discForm);
      setDiscounts([...discounts, data]);
      setDiscForm({ category: "", percent: 10, label: "", active: true });
      toast.success("Remise créée");
    } catch { toast.error("Erreur"); }
  };
  const toggleDiscount = async (d) => {
    const upd = { ...d, active: !d.active };
    await api.patch(`/admin/discounts/${d.id}`, upd);
    setDiscounts(discounts.map((x) => x.id === d.id ? upd : x));
  };
  const removeDiscount = async (id) => {
    await api.delete(`/admin/discounts/${id}`);
    setDiscounts(discounts.filter((d) => d.id !== id));
  };

  const NavItem = ({ id, icon: Icon, label, badge }) => (
    <button onClick={() => setTab(id)}
      className={`w-full text-left px-4 py-3 flex items-center gap-3 font-bold transition-colors ${tab===id?"bg-black text-white":"hover:bg-zinc-100"}`}
      data-testid={`nav-${id}`}>
      <Icon size={18} weight="bold" />
      <span className="flex-1">{label}</span>
      {badge != null && badge > 0 && (
        <span className={`text-[10px] px-1.5 py-0.5 ${tab===id?"bg-[#E60000]":"bg-[#E60000] text-white"}`}>{badge}</span>
      )}
    </button>
  );

  return (
    <div className="bg-zinc-50 min-h-screen" data-testid="admin-page">
      <div className="max-w-[1400px] mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-6">
          <ShieldCheck size={28} weight="duotone" className="text-[#E60000]" />
          <div>
            <h1 className="font-display font-black text-3xl tracking-tight">BackOffice</h1>
            <div className="text-xs text-zinc-500 uppercase tracking-wider">Easy Pièces · Administration</div>
          </div>
        </div>

        <div className="grid md:grid-cols-[240px_1fr] gap-6">
          {/* Sidebar */}
          <aside className="bg-white border-2 border-black h-fit">
            <NavItem id="dashboard" icon={ChartBar} label="Tableau de bord" />
            <NavItem id="orders" icon={Receipt} label="Commandes" badge={stats?.orders_to_process} />
            <NavItem id="quotes" icon={ChatCircleText} label="Devis" badge={stats?.quotes_pending} />
            <NavItem id="customers" icon={Users} label="Clients" />
            <NavItem id="discounts" icon={Tag} label="Remises" />
            <NavItem id="analytics" icon={ChartLineUp} label="Analytics" />
            <NavItem id="suppliers" icon={MagnifyingGlass} label="Fournisseurs" />
          </aside>

          {/* Content */}
          <main>
            {/* DASHBOARD */}
            {tab === "dashboard" && (
              <div data-testid="dashboard-tab">
                <h2 className="font-display font-extrabold text-2xl mb-4">Vue d'ensemble</h2>
                {stats && (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    <StatCard icon={CurrencyEur} label="Chiffre d'affaires" value={`${stats.revenue_total?.toFixed(2)} €`} accent />
                    <StatCard icon={Receipt} label="Commandes payées" value={stats.orders_paid} />
                    <StatCard icon={TruckTrailer} label="À expédier" value={stats.orders_to_process} highlight={stats.orders_to_process>0} />
                    <StatCard icon={ChatCircleText} label="Devis en attente" value={stats.quotes_pending} highlight={stats.quotes_pending>0} />
                    <StatCard icon={Users} label="Clients inscrits" value={stats.customers} />
                    <StatCard icon={Package} label="Cmds total" value={stats.orders_total} />
                    <StatCard icon={CheckCircle} label="Cmds livrées" value={stats.orders_shipped} />
                    <StatCard icon={ChatCircleText} label="Devis total" value={stats.quotes_total} />
                  </div>
                )}
                <div className="bg-white border-2 border-black p-5">
                  <h3 className="font-display font-extrabold text-lg mb-3">À traiter en priorité</h3>
                  {orders.filter(o => o.status === "paid").slice(0, 5).map(o => (
                    <div key={o.id} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-0">
                      <div><strong>{o.user_email}</strong> · {o.items?.length} articles · {o.amount?.toFixed(2)}€</div>
                      <button onClick={() => { setTab("orders"); setSelectedOrder(o); }} className="btn-secondary !py-1 !px-3 !text-xs">Traiter</button>
                    </div>
                  ))}
                  {!orders.some(o => o.status === "paid") && <p className="text-sm text-zinc-500">Aucune commande à expédier.</p>}
                </div>
              </div>
            )}

            {/* ORDERS */}
            {tab === "orders" && (
              <div data-testid="orders-tab">
                <h2 className="font-display font-extrabold text-2xl mb-4">Commandes ({orders.length})</h2>
                <div className="bg-white border-2 border-black overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-black text-white"><tr>
                      <th className="text-left p-3 text-xs uppercase tracking-wider">Date</th>
                      <th className="text-left p-3 text-xs uppercase tracking-wider">Client</th>
                      <th className="text-left p-3 text-xs uppercase tracking-wider">Articles</th>
                      <th className="text-left p-3 text-xs uppercase tracking-wider">Statut</th>
                      <th className="text-right p-3 text-xs uppercase tracking-wider">Total</th>
                      <th className="text-right p-3 text-xs uppercase tracking-wider"></th>
                    </tr></thead>
                    <tbody className="divide-y divide-zinc-100">
                      {orders.map((o) => (
                        <tr key={o.id} className="hover:bg-zinc-50" data-testid={`admin-order-${o.id}`}>
                          <td className="p-3 mono text-xs">{new Date(o.created_at).toLocaleDateString("fr-FR")}</td>
                          <td className="p-3"><strong>{o.user_email}</strong></td>
                          <td className="p-3">{o.items?.length}</td>
                          <td className="p-3">
                            <span className={`px-2 py-1 text-xs font-bold border-2 ${statusColor(o.status)}`}>
                              {statusLabel(o.status)}
                            </span>
                          </td>
                          <td className="p-3 text-right font-bold">{o.amount?.toFixed(2)} €</td>
                          <td className="p-3 text-right">
                            <button onClick={() => setSelectedOrder(o)} className="btn-ghost !text-xs underline">Voir</button>
                          </td>
                        </tr>
                      ))}
                      {orders.length===0 && <tr><td colSpan="6" className="p-8 text-center text-zinc-500">Aucune commande.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* QUOTES */}
            {tab === "quotes" && (
              <div data-testid="quotes-tab">
                <h2 className="font-display font-extrabold text-2xl mb-4">Demandes de devis ({quotes.length})</h2>
                <div className="bg-white border-2 border-black overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-black text-white"><tr>
                      <th className="text-left p-3 text-xs uppercase tracking-wider">Date</th>
                      <th className="text-left p-3 text-xs uppercase tracking-wider">Client</th>
                      <th className="text-left p-3 text-xs uppercase tracking-wider">Téléphone</th>
                      <th className="text-left p-3 text-xs uppercase tracking-wider">Produit</th>
                      <th className="text-left p-3 text-xs uppercase tracking-wider">Qté</th>
                      <th className="text-left p-3 text-xs uppercase tracking-wider">Statut</th>
                      <th className="text-right p-3 text-xs uppercase tracking-wider"></th>
                    </tr></thead>
                    <tbody className="divide-y divide-zinc-100">
                      {quotes.map((q) => (
                        <tr key={q.id} className="hover:bg-zinc-50" data-testid={`admin-quote-${q.id}`}>
                          <td className="p-3 mono text-xs">{new Date(q.created_at).toLocaleDateString("fr-FR")}</td>
                          <td className="p-3"><strong>{q.user_email}</strong></td>
                          <td className="p-3 mono text-xs">{q.phone || "—"}</td>
                          <td className="p-3">
                            <strong>{q.equipementier}</strong> {q.reference}<br/>
                            <span className="text-xs text-zinc-500">{q.designation}</span>
                          </td>
                          <td className="p-3">{q.quantity}</td>
                          <td className="p-3">
                            <span className={`px-2 py-1 text-xs font-bold border-2 ${q.status==="responded"?"border-[#00E676] text-[#00A050]":q.status==="won"?"border-emerald-500 text-emerald-700":q.status==="lost"?"border-zinc-400 text-zinc-500":"border-amber-400 text-amber-600"}`}>
                              {q.status || "pending"}
                            </span>
                          </td>
                          <td className="p-3 text-right">
                            <button onClick={() => setSelectedQuote(q)} className="btn-ghost !text-xs underline">Répondre</button>
                          </td>
                        </tr>
                      ))}
                      {quotes.length===0 && <tr><td colSpan="7" className="p-8 text-center text-zinc-500">Aucun devis.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* CUSTOMERS */}
            {tab === "customers" && (
              <div data-testid="customers-tab">
                <h2 className="font-display font-extrabold text-2xl mb-4">Clients ({customers.length})</h2>
                <div className="bg-white border-2 border-black overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-black text-white"><tr>
                      <th className="text-left p-3 text-xs uppercase tracking-wider">Inscrit le</th>
                      <th className="text-left p-3 text-xs uppercase tracking-wider">Nom</th>
                      <th className="text-left p-3 text-xs uppercase tracking-wider">Email</th>
                      <th className="text-left p-3 text-xs uppercase tracking-wider">Téléphone</th>
                      <th className="text-center p-3 text-xs uppercase tracking-wider">Commandes</th>
                      <th className="text-right p-3 text-xs uppercase tracking-wider">CA</th>
                    </tr></thead>
                    <tbody className="divide-y divide-zinc-100">
                      {customers.map((c) => (
                        <tr key={c.id} data-testid={`customer-${c.id}`}>
                          <td className="p-3 mono text-xs">{new Date(c.created_at).toLocaleDateString("fr-FR")}</td>
                          <td className="p-3 font-bold">{c.name}</td>
                          <td className="p-3">{c.email}</td>
                          <td className="p-3 mono text-xs">{c.phone || "—"}</td>
                          <td className="p-3 text-center">{c.orders_count}</td>
                          <td className="p-3 text-right font-bold text-[#E60000]">{c.lifetime_value?.toFixed(2)} €</td>
                        </tr>
                      ))}
                      {customers.length===0 && <tr><td colSpan="6" className="p-8 text-center text-zinc-500">Aucun client inscrit.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* DISCOUNTS */}
            {tab === "discounts" && (
              <div data-testid="discounts-tab">
                <h2 className="font-display font-extrabold text-2xl mb-4">Remises par catégorie</h2>
                <form onSubmit={addDiscount} className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-6 bg-white border-2 border-black p-4">
                  <input className="input-base md:col-span-2" placeholder="Catégorie (ex: Freinage)" value={discForm.category} onChange={(e)=>setDiscForm({...discForm,category:e.target.value})} data-testid="discount-category-input" />
                  <input className="input-base" type="number" min={0} max={90} placeholder="% Remise" value={discForm.percent} onChange={(e)=>setDiscForm({...discForm,percent:parseFloat(e.target.value)})} data-testid="discount-percent-input" />
                  <input className="input-base" placeholder="Libellé (opt.)" value={discForm.label} onChange={(e)=>setDiscForm({...discForm,label:e.target.value})} />
                  <button className="btn-primary flex items-center justify-center gap-2" data-testid="discount-submit-btn">
                    <Plus size={16} weight="bold" /> Ajouter
                  </button>
                </form>
                <div className="bg-white border-2 border-black divide-y-2 divide-black">
                  {discounts.length===0 && <div className="p-6 text-zinc-500">Aucune remise.</div>}
                  {discounts.map((d) => (
                    <div key={d.id} className="p-4 flex items-center justify-between" data-testid={`admin-discount-${d.id}`}>
                      <div>
                        <div className="font-display font-extrabold text-lg">{d.category}</div>
                        <div className="text-sm text-zinc-500">{d.label || `-${d.percent}%`}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="font-display font-black text-2xl text-[#E60000]">-{d.percent}%</div>
                        <button onClick={() => toggleDiscount(d)} className={`px-3 py-1 text-xs font-bold border-2 ${d.active?"border-[#00E676] text-[#00A050]":"border-zinc-300 text-zinc-400"}`}>{d.active?"ACTIVE":"INACTIVE"}</button>
                        <button onClick={() => removeDiscount(d.id)} className="text-zinc-400 hover:text-[#E60000]"><Trash size={20} weight="bold" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ANALYTICS */}
            {tab === "suppliers" && <SupplierSearchPanel />}

            {tab === "analytics" && (
              <div>
                <h2 className="font-display font-extrabold text-2xl mb-4">Analytics</h2>
                {!analytics && <p className="text-zinc-500 text-sm">Chargement…</p>}
                {analytics && (
                  <div className="space-y-6">
                    {/* KPIs */}
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                      <StatCard icon={Eye} label="Visites aujourd'hui" value={analytics.visits_today} />
                      <StatCard icon={Eye} label="Visites hier" value={analytics.visits_yesterday} />
                      <StatCard icon={ChartLineUp} label="Vues (30 jours)" value={analytics.total_views_30d} accent />
                    </div>

                    {/* Top pages */}
                    <div className="bg-white border-2 border-black p-5">
                      <div className="flex items-center gap-2 mb-4">
                        <Eye size={16} weight="bold" className="text-[#E60000]" />
                        <h3 className="font-display font-extrabold text-lg">Pages les plus consultées <span className="text-zinc-400 font-normal text-sm">(30 jours)</span></h3>
                      </div>
                      <table className="w-full text-sm">
                        <thead><tr className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
                          <th className="text-left p-2">Page</th>
                          <th className="text-right p-2">Visites</th>
                          <th className="text-right p-2">Chargement moy.</th>
                        </tr></thead>
                        <tbody className="divide-y divide-zinc-100">
                          {analytics.top_pages.map((p) => (
                            <tr key={p.path} className="hover:bg-zinc-50">
                              <td className="p-2 font-mono text-xs">{p.path}</td>
                              <td className="p-2 text-right font-bold">{p.views}</td>
                              <td className="p-2 text-right text-xs text-zinc-500">
                                {p.avg_load_ms ? (
                                  <span className={p.avg_load_ms > 2000 ? "text-red-600 font-bold" : p.avg_load_ms > 1000 ? "text-amber-600" : "text-green-600"}>
                                    {p.avg_load_ms < 1000 ? `${p.avg_load_ms} ms` : `${(p.avg_load_ms/1000).toFixed(1)} s`}
                                  </span>
                                ) : "—"}
                              </td>
                            </tr>
                          ))}
                          {analytics.top_pages.length === 0 && (
                            <tr><td colSpan={3} className="p-4 text-center text-zinc-400">Aucune donnée</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Time on page */}
                    <div className="bg-white border-2 border-black p-5">
                      <div className="flex items-center gap-2 mb-4">
                        <Clock size={16} weight="bold" className="text-[#E60000]" />
                        <h3 className="font-display font-extrabold text-lg">Temps moyen sur la page <span className="text-zinc-400 font-normal text-sm">(30 jours)</span></h3>
                      </div>
                      <table className="w-full text-sm">
                        <thead><tr className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
                          <th className="text-left p-2">Page</th>
                          <th className="text-right p-2">Sessions</th>
                          <th className="text-right p-2">Temps moyen</th>
                        </tr></thead>
                        <tbody className="divide-y divide-zinc-100">
                          {analytics.avg_time.map((p) => (
                            <tr key={p.path} className="hover:bg-zinc-50">
                              <td className="p-2 font-mono text-xs">{p.path}</td>
                              <td className="p-2 text-right text-zinc-500">{p.count}</td>
                              <td className="p-2 text-right font-bold">
                                {p.avg_s >= 60
                                  ? `${Math.floor(p.avg_s / 60)}m ${Math.round(p.avg_s % 60)}s`
                                  : `${p.avg_s}s`}
                              </td>
                            </tr>
                          ))}
                          {analytics.avg_time.length === 0 && (
                            <tr><td colSpan={3} className="p-4 text-center text-zinc-400">Aucune donnée</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Slow pages */}
                    {analytics.slow_pages.length > 0 && (
                      <div className="bg-white border-2 border-red-400 p-5">
                        <div className="flex items-center gap-2 mb-4">
                          <Warning size={16} weight="bold" className="text-red-500" />
                          <h3 className="font-display font-extrabold text-lg text-red-600">Pages lentes (&gt; 2s)</h3>
                        </div>
                        <table className="w-full text-sm">
                          <thead><tr className="bg-red-50 text-[10px] uppercase tracking-wider text-red-400">
                            <th className="text-left p-2">Page</th>
                            <th className="text-right p-2">Échantillons</th>
                            <th className="text-right p-2">Chargement moy.</th>
                          </tr></thead>
                          <tbody className="divide-y divide-zinc-100">
                            {analytics.slow_pages.map((p) => (
                              <tr key={p.path} className="hover:bg-red-50">
                                <td className="p-2 font-mono text-xs">{p.path}</td>
                                <td className="p-2 text-right text-zinc-500">{p.count}</td>
                                <td className="p-2 text-right font-bold text-red-600">
                                  {(p.avg_load_ms / 1000).toFixed(1)} s
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Order detail modal */}
      {selectedOrder && (
        <OrderDetailModal order={selectedOrder} onClose={() => setSelectedOrder(null)}
          onStatusChange={(s) => updateOrderStatus(selectedOrder.id, s)}
          onSave={(f) => saveOrderTracking(selectedOrder.id, f)}
          onRetryFerebam={() => retryFerebamOrder(selectedOrder.id)}
          onMarkReceived={() => markOrderReceived(selectedOrder.id)} />
      )}

      {/* Quote response modal */}
      {selectedQuote && (
        <QuoteResponseModal quote={selectedQuote} onClose={() => setSelectedQuote(null)}
          onSubmit={(f) => respondQuote(selectedQuote.id, f)} />
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent, highlight }) {
  return (
    <div className={`bg-white border-2 ${highlight?"border-[#E60000]":"border-black"} p-4 ${accent?"shadow-hard-red":""}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-1">
        <Icon size={14} weight="bold" /> {label}
      </div>
      <div className={`font-display font-black text-3xl ${accent?"text-[#E60000]":""}`}>{value}</div>
    </div>
  );
}

function OrderDetailModal({ order, onClose, onStatusChange, onSave, onRetryFerebam, onMarkReceived }) {
  const [tracking, setTracking] = useState(order.tracking_number || "");
  const [note, setNote] = useState(order.admin_note || "");
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border-2 border-black shadow-hard-red w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e)=>e.stopPropagation()} data-testid="order-detail-modal">
        <div className="p-5 border-b-2 border-black flex items-center justify-between">
          <div>
            <h3 className="font-display font-black text-2xl">Commande</h3>
            <div className="mono text-xs text-zinc-500">{order.id}</div>
          </div>
          <button onClick={onClose}><X size={22} weight="bold" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Client</div><div className="font-bold">{order.user_email}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Date</div><div>{new Date(order.created_at).toLocaleString("fr-FR")}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Total</div><div className="font-display font-black text-2xl text-[#E60000]">{order.amount?.toFixed(2)} €</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Paiement</div><div className="font-bold">{order.payment_status}</div></div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-2">Articles</div>
            <div className="border-2 border-zinc-200 divide-y divide-zinc-100">
              {order.items?.map((it, i) => (
                <div key={i} className="p-3 flex items-center gap-3">
                  {it.image && <img src={it.image} alt="" className="w-12 h-12 object-cover border" />}
                  <div className="flex-1">
                    {it.equipementier && <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">{it.equipementier}</div>}
                    <div className="font-bold text-sm">{it.designation}</div>
                    {it.reference && <div className="text-xs text-zinc-500 mono">Réf. {it.reference}</div>}
                  </div>
                  <div className="text-right text-sm">
                    <div>×{it.quantity}</div>
                    <div className="font-bold">{(it.unit_price * it.quantity).toFixed(2)} €</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-2">Statut</div>
            <div className="flex flex-wrap gap-2">
              {ORDER_STATUS.map(s => (
                <button key={s.v} onClick={() => onStatusChange(s.v)}
                  className={`px-3 py-2 text-xs font-bold border-2 ${order.status===s.v?"bg-black text-white border-black":`${s.color} hover:bg-zinc-50`}`}
                  data-testid={`set-status-${s.v}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">N° de suivi</label>
              <input className="input-base" value={tracking} onChange={(e)=>setTracking(e.target.value)} placeholder="Ex: 1Z999AA10123456784" data-testid="tracking-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Note interne</label>
              <input className="input-base" value={note} onChange={(e)=>setNote(e.target.value)} placeholder="Note (visible admin uniquement)" />
            </div>
          </div>
          {/* Odoo procurement and fulfilment */}
          <div className="border-2 border-zinc-200 p-3 space-y-2">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Back-office RM Location · Odoo</div>
            <div className="flex items-center gap-4 text-sm">
              <div>
                <span className="text-zinc-500">Vente : </span>
                <span className="font-bold mono">{order.odoo_sale_order_name || "Non synchronisée"}</span>
              </div>
              <div>
                <span className="text-zinc-500">Étape : </span>
                <span className={`font-bold ${order.odoo_fulfillment_state === "delivered" ? "text-green-600" : order.odoo_sync_status === "error" ? "text-red-600" : "text-blue-600"}`}>
                  {{
                    customer_received: "Commande client reçue",
                    supplier_draft: "Achat fournisseur à valider",
                    supplier_ordered: "Commandée au fournisseur",
                    received: "Réceptionnée — prête à livrer",
                    delivered: "Livrée au client",
                    cancelled: "Annulée",
                  }[order.odoo_fulfillment_state] || (order.odoo_sync_status === "error" ? "Erreur de synchronisation" : "En attente")}
                </span>
              </div>
            </div>
            {!!order.odoo_purchase_order_names?.length && (
              <div className="text-sm"><span className="text-zinc-500">Achats : </span><span className="font-bold mono">{order.odoo_purchase_order_names.join(", ")}</span></div>
            )}
            <div className="flex gap-2 flex-wrap">
              {onRetryFerebam && (
                <button onClick={onRetryFerebam} className="px-3 py-1 text-xs font-bold border-2 border-blue-400 text-blue-600 hover:bg-blue-50">
                  ↺ Synchroniser avec Odoo
                </button>
              )}
              {order.odoo_url && (
                <a href={order.odoo_url} target="_blank" rel="noreferrer" className="px-3 py-1 text-xs font-bold border-2 border-black hover:bg-black hover:text-white">
                  Ouvrir dans Odoo ↗
                </a>
              )}
            </div>
            <p className="text-xs text-zinc-500">La commande fournisseur est créée en brouillon. Sa validation, la réception et la livraison se font dans Odoo.</p>
          </div>
          <button onClick={() => onSave({ tracking_number: tracking, admin_note: note })} className="btn-primary w-full" data-testid="save-order-btn">Enregistrer</button>
        </div>
      </div>
    </div>
  );
}

function QuoteResponseModal({ quote, onClose, onSubmit }) {
  const [price, setPrice] = useState(quote.proposed_price || "");
  const [response, setResponse] = useState(quote.admin_response || "");
  const [status, setStatus] = useState(quote.status || "pending");
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border-2 border-black shadow-hard-red w-full max-w-xl" onClick={(e)=>e.stopPropagation()} data-testid="quote-detail-modal">
        <div className="p-5 border-b-2 border-black flex items-center justify-between">
          <h3 className="font-display font-black text-2xl">Répondre au devis</h3>
          <button onClick={onClose}><X size={22} weight="bold" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-zinc-50 border border-zinc-200 p-3 text-sm">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-1">Client</div>
            <div><strong>{quote.user_email}</strong> {quote.phone && <>· {quote.phone}</>}</div>
            <div className="mt-2 text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-1">Produit demandé</div>
            <div className="font-bold">{quote.equipementier} {quote.reference}</div>
            <div className="text-xs text-zinc-600">{quote.designation} · Qté {quote.quantity}</div>
            {quote.message && <div className="mt-2 italic text-zinc-600">« {quote.message} »</div>}
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Prix proposé (€)</label>
            <input className="input-base" type="number" step="0.01" value={price} onChange={(e)=>setPrice(e.target.value)} placeholder="Ex: 45.90" data-testid="quote-price-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Réponse au client</label>
            <textarea className="input-base" rows={4} value={response} onChange={(e)=>setResponse(e.target.value)} placeholder="Ex: Bonjour, après vérification..." data-testid="quote-response-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Statut</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {["pending","responded","won","lost"].map(s => (
                <button key={s} onClick={() => setStatus(s)} className={`px-3 py-2 text-xs font-bold border-2 ${status===s?"bg-black text-white border-black":"border-zinc-300 hover:bg-zinc-50"}`} data-testid={`quote-status-${s}`}>
                  {s==="pending"?"En attente":s==="responded"?"Répondu":s==="won"?"Gagné":"Perdu"}
                </button>
              ))}
            </div>
          </div>
          <button onClick={() => onSubmit({ proposed_price: price ? parseFloat(price) : null, admin_response: response, status })} className="btn-primary w-full" data-testid="save-quote-btn">
            Enregistrer la réponse
          </button>
        </div>
      </div>
    </div>
  );
}
